import React, { useState } from 'react';

import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@editor/stores/planSelectionStore';
import { subscriptionApi } from '@repo/subscription/api';
import { Button } from '@repo/ui/components/Buttons';
import { fireToast } from '@repo/ui/components/Toast';

import useBillingPrices from '../../hooks/useBillingPrices';

import Confirmations from './Confirmations';
import { ProceedButtonWrapper } from './styles';
import { ActionProps } from './types';

/**
 * This component renders a button that allows the user to proceed to the checkout process.
 * It handles the logic for checking if the subscription can be created and displays appropriate messages based on the response.
 */
const ProceedToCheckoutButton: React.FC<ActionProps> = ({
  disabled,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const promoCode = usePlanSelectionStore(planSelectionStoreSelector.promoCode);

  const pricesToChange = useBillingPrices();

  // If the prices are not available, return null
  if (!pricesToChange) {
    return null;
  }

  const handleProceed = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await subscriptionApi.canCreateSubscription({
        promoCode: promoCode?.code,
        localPrices: pricesToChange,
      });

      if (response?.success === false) {
        throw new Error(response.error);
      }
      onSuccess();
    } catch (e) {
      console.error(e);
      fireToast({
        label: e instanceof Error ? e.message : `${e}`,
        error: true,
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ProceedButtonWrapper>
      <Button
        dataTestId="subscriptionModal--proceed-to-checkout-button"
        disabled={disabled}
        label="Upgrade Now"
        loading={isLoading}
        name="proceedToCheckoutButton"
        width="100%"
        onClick={handleProceed}
      />
      <Confirmations />
    </ProceedButtonWrapper>
  );
};

export default ProceedToCheckoutButton;
